import { supabase } from "@/integrations/supabase/client";

const TARGET_TENANT_SLUG = "liplaundry";
const TARGET_FACTORY_CODE = "LIPFACTORY";
const ACTION = "factory_auth_diagnostic";
const RESOURCE_TYPE = "factory_portal_auth";
const DEDUPE_WINDOW_MS = 750;

let lastEventKey = "";
let lastEventAt = 0;

type DiagnosticDetails = Record<string, unknown>;

function readFactoryContext() {
  try {
    const raw = window.localStorage.getItem("lysara_factory_login");
    const tenantId = window.localStorage.getItem("selectedTenantId");
    if (!raw) return { tenantId, factoryId: null, factoryCode: null };
    const parsed = JSON.parse(raw) as {
      factoryId?: unknown;
      factoryData?: { id?: unknown; factory_code?: unknown };
    };
    return {
      tenantId,
      factoryId: typeof parsed.factoryId === "string" ? parsed.factoryId :
        typeof parsed.factoryData?.id === "string" ? parsed.factoryData.id : null,
      factoryCode: typeof parsed.factoryData?.factory_code === "string"
        ? parsed.factoryData.factory_code : null,
    };
  } catch {
    return { tenantId: null, factoryId: null, factoryCode: null };
  }
}

function isTargetContext(context: ReturnType<typeof readFactoryContext>): boolean {
  return context.factoryCode?.toUpperCase() === TARGET_FACTORY_CODE &&
    window.localStorage.getItem("selectedTenantSlug")?.toLowerCase() === TARGET_TENANT_SLUG;
}

function safeErrorDetails(error: unknown): DiagnosticDetails {
  if (!error || typeof error !== "object") return { error: String(error ?? "unknown") };
  const candidate = error as Record<string, unknown>;
  const details: DiagnosticDetails = {};
  for (const key of ["status", "code", "name", "message"]) {
    const value = candidate[key];
    if (typeof value === "string" || typeof value === "number") details[key] = value;
  }
  return details;
}

export async function logFactoryAuthDiagnostic(
  event: string,
  details: DiagnosticDetails = {},
): Promise<void> {
  if (typeof window === "undefined") return;
  const context = readFactoryContext();
  if (!isTargetContext(context)) return;

  const eventKey = event + ":" + JSON.stringify(details);
  const now = Date.now();
  if (eventKey === lastEventKey && now - lastEventAt < DEDUPE_WINDOW_MS) return;
  lastEventKey = eventKey;
  lastEventAt = now;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id || !context.tenantId) return;
    await (supabase.from("audit_logs") as any).insert({
      tenant_id: context.tenantId,
      user_id: session.user.id,
      resource_type: RESOURCE_TYPE,
      resource_id: context.factoryId,
      action: ACTION,
      new_values: {
        event,
        details,
        pathname: window.location.pathname,
        online: navigator.onLine,
        visibility: document.visibilityState,
        user_agent: navigator.userAgent.slice(0, 240),
        client_at: new Date(now).toISOString(),
      },
    });
  } catch {
    // Diagnostics must never alter authentication behavior.
  }
}

export function diagnosticErrorDetails(error: unknown): DiagnosticDetails {
  return safeErrorDetails(error);
}
